/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaTreeMoves.ts
 * FILE_ID: JOBHUB-QA-TREE-MOVES-HOOK-001
 * PURPOSE: Move and reorder QA tree nodes, and undo the last move.
 * WHY: A move is the user teaching the tree something the quote never said, so
 *      getting it wrong must cost one click, not a re-parse.
 * LANDMINE: Undo replays the response's `from_parent_id`, never a parent the
 *      caller remembered. The backend renames on collision, so the row that
 *      came back is the only accurate record of where each node was.
 * RELATED: QaMoveDialog.tsx, qa/services/tree_moves.py
 */
import { useState } from 'react'
import { useMutation, type QueryClient } from '@tanstack/react-query'

import type { UndoEntry } from '@quotech/ui'
import type { QaApi, QaArea, QaAreaExecution, QaFloor, QaMoveResult, QaTreeNodeType } from '../../qa/qaApi'
import { qaErrorMessage } from './qaTabSelectors'
import { holdQaArea, releaseQaArea } from './qaExecutionCache'
import { placeQaMove } from './qaSelectionWrites'

/** Enough to put every node back where it was, grouped by its old parent. */
export interface QaUndoableMove {
  nodeType: QaTreeNodeType
  parentName: string
  byParent: { parentId: string; nodeIds: string[] }[]
}

function undoOf(result: QaMoveResult): QaUndoableMove | null {
  if (result.moves.length === 0) return null
  const byParent = new Map<string, string[]>()
  for (const move of result.moves) {
    const bucket = byParent.get(move.from_parent_id)
    if (bucket) bucket.push(move.id)
    else byParent.set(move.from_parent_id, [move.id])
  }
  return {
    nodeType: result.node_type,
    parentName: result.parent_name,
    byParent: [...byParent].map(([parentId, nodeIds]) => ({ parentId, nodeIds })),
  }
}

export function useQaTreeMoves({
  api,
  inspectionId,
  areaId,
  queryClient,
  registerUndo,
}: {
  api: QaApi
  inspectionId: string | null
  areaId: string | null
  queryClient: QueryClient
  registerUndo?: (entry: UndoEntry) => void
}) {
  const [error, setError] = useState<string | null>(null)
  const [undoable, setUndoable] = useState<QaUndoableMove | null>(null)

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['qa-areas', inspectionId] })
    queryClient.invalidateQueries({ queryKey: ['qa-floors', inspectionId] })
    queryClient.invalidateQueries({ queryKey: ['qa-area-exec', areaId] })
    queryClient.invalidateQueries({ queryKey: ['qa-inspections'] })
  }

  const snapshotMove = () => ({
    areas: queryClient.getQueryData(['qa-areas', inspectionId]),
    floors: queryClient.getQueryData(['qa-floors', inspectionId]),
    exec: queryClient.getQueriesData<QaAreaExecution>({ queryKey: ['qa-area-exec'] }),
  })
  const restoreMove = (snapshot: ReturnType<typeof snapshotMove>) => {
    queryClient.setQueryData(['qa-areas', inspectionId], snapshot.areas)
    queryClient.setQueryData(['qa-floors', inspectionId], snapshot.floors)
    for (const [key, value] of snapshot.exec) queryClient.setQueryData(key, value)
  }
  const sourceAreaId = (checklistId: string) => {
    const rows = queryClient.getQueriesData<QaAreaExecution>({ queryKey: ['qa-area-exec'] })
    return rows.find(([, value]) => value?.checklists?.some((row) => row.id === checklistId))?.[1]?.id
  }

  const move = useMutation({
    mutationFn: ({
      nodeType,
      nodeIds,
      destination,
    }: {
      nodeType: QaTreeNodeType
      nodeIds: string[]
      destination: { parentId: string } | { newLevelName: string }
    }) => api.moveTreeNodes(nodeType, nodeIds, destination),
    onMutate: ({ nodeType, nodeIds, destination }) => {
      const snapshot = snapshotMove()
      const held = new Set<string>()
      const hold = (id: string | null | undefined) => {
        if (!id || held.has(id)) return
        holdQaArea(id)
        held.add(id)
      }
      hold(areaId)
      if (nodeType === 'checklist_instance' && 'parentId' in destination) hold(destination.parentId)
      for (const id of nodeIds) {
        if (nodeType === 'checklist_instance') hold(sourceAreaId(id))
      }
      if (nodeType === 'area' && 'parentId' in destination) {
        placeQaMove(queryClient, inspectionId, {
          node_type: 'area',
          parent_id: destination.parentId,
          parent_name: '',
          created_parent: false,
          moves: nodeIds.map((id, index) => ({ id, from_parent_id: '', to_parent_id: destination.parentId, sort_order: index })),
        })
      } else if (nodeType === 'area' && 'newLevelName' in destination) {
        placeQaMove(queryClient, inspectionId, {
          node_type: 'area',
          parent_id: 'pending-level',
          parent_name: destination.newLevelName,
          created_parent: true,
          moves: nodeIds.map((id, index) => ({ id, from_parent_id: '', to_parent_id: 'pending-level', sort_order: index })),
        })
      } else if (nodeType === 'checklist_instance' && 'parentId' in destination) {
        placeQaMove(queryClient, inspectionId, {
          node_type: 'checklist_instance',
          parent_id: destination.parentId,
          parent_name: '',
          created_parent: false,
          moves: nodeIds.map((id) => ({
            id,
            from_parent_id: sourceAreaId(id) ?? '',
            to_parent_id: destination.parentId,
            sort_order: 0,
          })),
        })
      }
      return { snapshot, held: [...held] }
    },
    onSuccess: (result, variables, context) => {
      setError(null)
      placeQaMove(queryClient, inspectionId, result)
      for (const id of context?.held ?? []) releaseQaArea(id)
      const undone = undoOf(result)
      setUndoable(undone)
      if (undone) {
        registerUndo?.({
          label: 'Move',
          undo: async () => {
            for (const group of undone.byParent) {
              await api.moveTreeNodes(undone.nodeType, group.nodeIds, { parentId: group.parentId })
            }
          },
          redo: () => api.moveTreeNodes(variables.nodeType, variables.nodeIds, variables.destination),
        })
      }
    },
    onError: (cause: unknown, _variables, context) => {
      if (context?.snapshot) restoreMove(context.snapshot)
      for (const id of context?.held ?? []) releaseQaArea(id)
      setError(qaErrorMessage(cause, 'That move could not be saved.'))
    },
  })

  const undo = useMutation({
    mutationFn: async (target: QaUndoableMove) => {
      // One call per original parent: the backend moves a selection to ONE
      // destination, and a selection can have come from several.
      const results: QaMoveResult[] = []
      for (const group of target.byParent) {
        results.push(await api.moveTreeNodes(target.nodeType, group.nodeIds, { parentId: group.parentId }))
      }
      return results
    },
    onMutate: (target) => {
      const snapshot = snapshotMove()
      const held = new Set<string>()
      for (const group of target.byParent) {
        if (held.has(group.parentId)) continue
        holdQaArea(group.parentId)
        held.add(group.parentId)
      }
      return { snapshot, held: [...held] }
    },
    onSuccess: (results, _target, context) => {
      setError(null)
      setUndoable(null)
      for (const result of results) placeQaMove(queryClient, inspectionId, result)
      for (const id of context?.held ?? []) releaseQaArea(id)
    },
    onError: (cause: unknown, _target, context) => {
      if (context?.snapshot) restoreMove(context.snapshot)
      for (const id of context?.held ?? []) releaseQaArea(id)
      setError(qaErrorMessage(cause, 'That move could not be undone.'))
    },
  })

  const reorder = useMutation({
    mutationFn: ({
      nodeType,
      order,
    }: {
      nodeType: QaTreeNodeType
      order: { id: string; sort_order: number }[]
    }) => api.reorderTreeNodes(nodeType, order),
    onMutate: ({ nodeType, order }) => {
      const snapshot = snapshotMove()
      const held: string[] = []
      if (nodeType === 'checklist_instance' && areaId) {
        holdQaArea(areaId)
        held.push(areaId)
      }
      applyTreeReorder(queryClient, inspectionId, areaId, nodeType, order)
      return { snapshot, held }
    },
    onSuccess: (_result, _variables, context) => {
      setError(null)
      for (const id of context?.held ?? []) releaseQaArea(id)
      refresh()
    },
    onError: (cause: unknown, _variables, context) => {
      if (context?.snapshot) restoreMove(context.snapshot)
      for (const id of context?.held ?? []) releaseQaArea(id)
      setError(qaErrorMessage(cause, 'That reorder could not be saved.'))
    },
  })

  return {
    error,
    dismissError: () => setError(null),
    move,
    reorder,
    undo,
    undoable,
    dismissUndo: () => setUndoable(null),
  }
}

function rankRows<T extends { id: string; sort_order?: number }>(
  rows: T[] | undefined,
  order: { id: string; sort_order: number }[],
): T[] | undefined {
  if (!rows) return rows
  const rank = new Map(order.map((row) => [row.id, row.sort_order]))
  if (!rows.some((row) => rank.has(row.id))) return rows
  return rows
    .map((row) => {
      const sort = rank.get(row.id)
      return sort == null ? row : { ...row, sort_order: sort }
    })
    .sort((left, right) => (left.sort_order ?? 0) - (right.sort_order ?? 0))
}

/** Write a sibling reorder into the QA caches before the request returns. */
export function applyTreeReorder(
  queryClient: QueryClient,
  inspectionId: string | null,
  areaId: string | null,
  nodeType: QaTreeNodeType,
  order: { id: string; sort_order: number }[],
): void {
  if (nodeType === 'area') {
    const areas = queryClient.getQueryData<QaArea[]>(['qa-areas', inspectionId])
    if (areas) queryClient.setQueryData(['qa-areas', inspectionId], rankRows(areas, order))
    const floors = queryClient.getQueryData<QaFloor[]>(['qa-floors', inspectionId])
    if (floors) {
      queryClient.setQueryData(['qa-floors', inspectionId], floors.map((floor) => ({
        ...floor,
        areas: rankRows(floor.areas, order) ?? floor.areas,
      })))
    }
    return
  }
  if (nodeType === 'checklist_instance') {
    const execution = queryClient.getQueryData<QaAreaExecution>(['qa-area-exec', areaId])
    if (!execution) return
    queryClient.setQueryData(['qa-area-exec', areaId], {
      ...execution,
      checklists: rankRows(execution.checklists, order) ?? execution.checklists,
    })
  }
}

/** Swap a node with its neighbour, as the up/down arrows do. */
export function swappedOrder<T extends { id: string }>(
  siblings: readonly T[],
  index: number,
  direction: -1 | 1,
): { id: string; sort_order: number }[] {
  const target = index + direction
  if (index < 0 || index >= siblings.length || target < 0 || target >= siblings.length) return []
  // The WHOLE sibling list is renumbered, not just the swapped pair.
  // Quote-generated rows routinely share a sort_order, and exchanging two equal
  // numbers is a no-op that reads as a broken button.
  const next = [...siblings]
  ;[next[index], next[target]] = [next[target], next[index]]
  return next.map((node, position) => ({ id: node.id, sort_order: position }))
}
